<template>
  <div class="ProductListPage Pt75">
    <div class="box">
      <div class="page-container">

        <!-- PC 端侧边栏 -->
        <aside class="sidebar" v-if="!isMobile">
          <div v-for="block in sidebarLinks" :key="block.title" class="sidebar-block">
            <h3>{{ block.title }}</h3>
            <ul>
              <li v-for="link in block.links" :key="link.name">
                <a
                    href="#"
                    :class="{ active: activeCategory === link.name }"
                    @click.prevent="selectCategory(link.name, block.title, link.id)"
                >
                  {{ link.name }}
                </a>
              </li>
            </ul>
          </div>
        </aside>

        <!-- 移动端 Tab 结构 -->
        <div class="tab-container" v-else>
          <!-- 一级 Tab -->
          <div class="primary-tabs">
            <div
                v-for="block in sidebarLinks"
                :key="block.title"
                class="primary-tab"
                :class="{ active: activeTab === block.title }"
                @click="selectPrimaryTab(block.title)"
            >
              {{ block.title }}
            </div>
          </div>
          <!-- 二级 Tab -->
          <div class="secondary-tabs" v-if="currentBlockLinks.length">
            <div
                v-for="link in currentBlockLinks"
                :key="link.name"
                class="secondary-tab"
                :class="{ active: activeCategory === link.name }"
                @click="selectCategory(link.name, activeTab, link.id)"
            >
              {{ link.name }}
            </div>
          </div>
        </div>

        <main class="main-content">
          <div class="content-header">
            <div class="header-left">
              <nav aria-label="breadcrumb" class="breadcrumbs">
                <a href="#">首页</a>
                <span class="divider">/</span>
                <a href="#">{{ parentCategory }}</a>
                <span class="divider">/</span>
                <span>{{ pageTitle }}</span>
              </nav>
              <h2 class="category-title">{{ pageTitle }}</h2>
            </div>
            <div class="header-right">
              <span class="product-count">
                共 <strong>{{ filteredProducts.length }}</strong> 件商品
              </span>
            </div>
          </div>

          <ProductDetail
              v-if="selectedProduct"
              :product="selectedProduct"
              @back-to-list="hideProductDetail"
          />

          <div v-else class="product-list-wrapper">
            <div class="product-grid">
              <div
                  v-for="product in filteredProducts"
                  :key="product.id"
                  class="product-item-card"
                  @click="showProductDetail(product)"
              >
                <div class="product-image">
                  <img :src="product.cover_image_path" :alt="product.name"/>
                  <a href="#" @click.prevent="showProductDetailWithRouter(product)" class="view-details-btn">查看详情</a>
                </div>
                <div class="product-info">
                  <h4>{{ product.name }}</h4>
                  <div class="product-specs">
                    <p v-for="(value, key) in product.technical_parameters" :key="key">{{ key }}: {{ value }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="filteredProducts.length === 0" class="no-products-found">
              <p>正在加载...</p>
            </div>
          </div>
        </main>
      </div>
    </div>
  </div>
</template>

<script>
import ProductDetail from '@/views/page/ProductDetail.vue';
import {getProductsCategory, getProductList} from '@/api/request.js';

export default  {
  name: 'ProductListPage',
  components: {
    ProductDetail,
  },
  data() {
    return {
      activeCategory: '', // 默认激活的二级分类
      pageTitle: '',     // 页面标题
      parentCategory: '', // 用于面包屑导航
      activeTab: '',     // 默认激活的一级 Tab
      selectedProduct: null, // 跟踪选中的产品详情
      sidebarLinks: [],     // 动态获取的分类数据
      allProducts: [],      // 动态获取的产品数据
      isMobile: false,      // 跟踪是否为移动端
      loading: false,       // 新增：加载状态
    };
  },
  computed: {
    filteredProducts() {
      if (!this.activeCategory) return [];
      // The selectCategory method already fetches products for the active category,
      // so this.allProducts is expected to contain the correctly filtered list.
      return this.allProducts;
    },
    currentBlockLinks() {
      const block = this.sidebarLinks.find(b => b.title === this.activeTab);
      return block ? block.links : [];
    },
  },
  methods: {
    async fetchData() {
      try {
        const categoriesResponse = await getProductsCategory();
        this.sidebarLinks = categoriesResponse.list
            .filter(category => category.parent_id === 0)
            .map(category => ({
              title: category.name,
              links: categoriesResponse.list
                  .filter(sub => sub.parent_id === category.id)
                  .map(sub => ({
                    name: sub.name,
                    id: sub.id
                  }))
            }));
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    },
    async selectCategory(categoryName, parentTitle, categoryId) {
      this.loading = true;
      this.activeCategory = categoryName;
      this.activeCategoryId = categoryId;
      this.pageTitle = categoryName;
      this.parentCategory = parentTitle;
      this.selectedProduct = null;
      this.activeTab = parentTitle;

      // 清除URL中的productId参数
      if (this.$route.query.productId) {
        this.$router.replace({
          params: { categoryId: categoryId },
          query: {} // 清除查询参数
        });
      }

      try {
        const productsResponse = await getProductList({ "category_id": categoryId });
        this.allProducts = productsResponse.list.map(product => ({
          id: product.id,
          category_id: product.category_id,
          name: product.name,
          images: [product.cover_image_url, ...(product.detail_images_urls || [])],
          technical_parameters: product.technical_parameters || {},
          model: product.product_code,
          features: product.features || [],
          cover_image_path: product.cover_image_url,
          overview: product.overview,
          application_fields: product.application_fields,
          application_scenarios: product.application_scenarios,
          related_materials: product.related_materials,
        }));
      } catch (error) {
        console.error('Failed to fetch products:', error);
      } finally {
        this.loading = false;
      }
    },
    showProductDetail(product) {
      this.selectedProduct = product;
      window.scrollTo(0, 0);
    },
    hideProductDetail() {
      this.selectedProduct = null;
    },
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
    async handleRouteChange(route) {
      const categoryIdFromUrl = route.params.categoryId;
      let targetLink = null;
      let parentBlock = null;

      if (categoryIdFromUrl) {
        for (const block of this.sidebarLinks) {
          const foundLink = block.links.find(link => link.id == categoryIdFromUrl);
          if (foundLink) {
            targetLink = foundLink;
            parentBlock = block;
            break;
          }
        }
      } else {
        if (this.sidebarLinks.length > 0 && this.sidebarLinks[0].links.length > 0) {
          parentBlock = this.sidebarLinks[0];
          targetLink = parentBlock.links[0];
        }
      }

      if (targetLink && parentBlock) {
        // Only refetch products if the category has changed.
        if (this.activeCategoryId !== targetLink.id) {
          await this.selectCategory(targetLink.name, parentBlock.title, targetLink.id);
        }

        const productIdFromUrl = route.query.productId;
        if (productIdFromUrl) {
          const product = this.allProducts.find(p => p.id == productIdFromUrl);
          if (product) {
            // Only update if the selected product is different
            if (!this.selectedProduct || this.selectedProduct.id != product.id) {
              this.showProductDetail(product);
            }
          } else {
            // Product ID is invalid or not in this category, show list.
            this.hideProductDetail();
          }
        } else {
          // No product ID in URL, ensure detail view is hidden.
          this.hideProductDetail();
        }
      } else {
        console.warn(`No valid category found for ID: ${categoryIdFromUrl}`);
      }
    },
    showProductDetailWithRouter(product) {
      // 更新路由，添加productId查询参数
      this.$router.replace({
        params: { categoryId: this.activeCategoryId },
        query: { productId: product.id }
      });
      
      // 显示产品详情
      this.showProductDetail(product);
    },
    selectPrimaryTab(tabTitle) {
      this.activeTab = tabTitle;
      // Automatically select the first secondary tab of the chosen primary tab
      const block = this.sidebarLinks.find(b => b.title === tabTitle);
      if (block && block.links.length > 0) {
        const firstLink = block.links[0];
        this.selectCategory(firstLink.name, tabTitle, firstLink.id);
      }
    },
  },
  watch: {
    '$route'(to) {
      // React to route changes, e.g., productId change, category change.
      this.handleRouteChange(to);
    }
  },
  async mounted() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);

    // 1. Fetch the static category structure once.
    await this.fetchData();

    // 2. Handle the initial route setup.
    await this.handleRouteChange(this.$route);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
  },
};
</script>

<style scoped>
/* 样式保持不变 */
.Pt75 {
  padding-bottom: 75px;
  background-color: #f8f9fa;
}

.box {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 16px;
}

/* Page Layout */
.page-container {
  display: flex;
  gap: 30px;
  align-items: flex-start;
}

/* Sidebar Styles */
.sidebar {
  width: 240px;
  flex-shrink: 0;
}

.sidebar-block {
  background-color: #fff;
  border-radius: 8px;
  padding: 15px 0;
  margin-bottom: 20px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
}

.sidebar-block h3 {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  background-color: #47975a;
  padding: 12px 20px;
  margin: -15px 0 10px 0;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.sidebar-block ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.sidebar-block li a {
  display: block;
  padding: 12px 20px;
  font-size: 15px;
  color: #333;
  text-decoration: none;
  transition: all 0.2s ease-in-out;
  border-left: 3px solid transparent;
}

.sidebar-block li a:hover {
  background-color: #f0f8f4;
  color: #47975a;
  border-left-color: #47975a;
}

.sidebar-block li a.active {
  background-color: #343a40;
  color: #fff;
  border-left-color: #47975a;
  font-weight: 500;
}

/* Tab Container (Basic styles, detailed in mobile-styles.css) */
.tab-container {
  display: none; /* 默认隐藏，移动端显示 */
}

/* Main Content Styles */
.main-content {
  flex-grow: 1;
  width: 100%;
}

/* Content Header Styles */
.content-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  background-color: #fff;
  padding: 20px 25px;
  border-radius: 12px;
  margin-bottom: 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
}

.header-left {
  flex-grow: 1;
}

.breadcrumbs {
  margin-bottom: 8px;
  font-size: 14px;
}

.breadcrumbs a {
  color: #6c757d;
  text-decoration: none;
  transition: color 0.2s;
}

.breadcrumbs a:hover {
  color: #47975a;
}

.breadcrumbs span {
  color: #343a40;
  font-weight: 500;
}

.breadcrumbs .divider {
  margin: 0 8px;
  color: #ced4da;
}

.category-title {
  font-size: 26px;
  color: #333;
  margin: 0;
  font-weight: 700;
  display: flex;
  align-items: center;
}

.category-title::before {
  content: '';
  display: inline-block;
  width: 5px;
  height: 24px;
  background-color: #47975a;
  margin-right: 12px;
  border-radius: 2px;
}

.header-right {
  flex-shrink: 0;
}

.product-count {
  font-size: 15px;
  color: #6c757d;
  background-color: #f8f9fa;
  padding: 8px 15px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

.product-count strong {
  color: #343a40;
  font-weight: 600;
}

/* Product Grid Styles */
.product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 30px;
}

/* Product Card Styles */
.product-item-card {
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.07);
  overflow: hidden;
  transition: all 0.3s ease;
  border: 1px solid #e9ecef;
}

.product-item-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
}

.product-image {
  position: relative;
  background-color: #FFF;
  padding: 20px;
  height: 250px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-image img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.view-details-btn {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  padding: 10px 20px;
  border-radius: 20px;
  text-decoration: none;
  font-weight: 500;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.product-item-card:hover .view-details-btn {
  opacity: 1;
}

.product-info {
  padding: 20px;
  text-align: center;
}

.product-info h4 {
  font-size: 16px;
  color: #333;
  margin: 0 0 10px 0;
  font-weight: 600;
}

.product-specs p {
  font-size: 14px;
  color: #666;
  margin: 5px 0 0;
  line-height: 1.5;
}

.no-products-found {
  padding: 80px 20px;
  text-align: center;
  background: #fff;
  border-radius: 12px;
  margin-top: 30px;
}

.no-products-found p {
  font-size: 18px;
  color: #888;
}

/* Responsive Design */
@media (max-width: 992px) {
  .page-container {
    flex-direction: column;
  }

  .sidebar {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 20px;
  }

  .sidebar-block {
    margin-bottom: 0;
  }
}

@media (max-width: 576px) {
  .content-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .category-title {
    font-size: 22px;
  }

  .product-grid {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 20px;
  }
}
</style>